import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { CircularProgress, Grid } from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  type Nil,
  type Question,
  QuestionView,
  Text,
} from '@pbt/pbt-ui-components'

import FeatureToggle from '~/constants/featureToggle'
import { fetchPreviewQuestion } from '~/store/actions/questions'
import {
  getAnswerInputTypes,
  getFeatureToggle,
} from '~/store/reducers/constants'
import {
  getPreviewQuestion,
  getQuestionsIsPreviewing,
} from '~/store/reducers/questions'

import PuiPopup, { type PuiPopupProps } from './PuiPopup'

const StyledContainer = styled(Grid)({
  width: 600,
})

const StyledSpinner = styled(Grid)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 200,
})

export interface PreviewQuestionPopupProps extends PuiPopupProps {
  question: Question | Nil
}

const PreviewQuestionPopup = ({
  question,
  open,
  ...rest
}: PreviewQuestionPopupProps) => {
  const dispatch = useDispatch()
  const AnswerInputTypes = useSelector(getAnswerInputTypes)
  const isPreviewing = useSelector(getQuestionsIsPreviewing)
  const previewQuestion = useSelector(getPreviewQuestion)
  const isQuestionsDynamicTextAndSubtextEnabled = useSelector(
    getFeatureToggle(FeatureToggle.QUESTIONS_DYNAMIC_TEXT_AND_SUBTEXT),
  )
  const { t } = useTranslation('Admin')

  useEffect(() => {
    if (open && question && isQuestionsDynamicTextAndSubtextEnabled) {
      dispatch(fetchPreviewQuestion(question))
    }
  }, [open, question, isQuestionsDynamicTextAndSubtextEnabled])

  const renderQuestionContent = () => {
    if (!isQuestionsDynamicTextAndSubtextEnabled) {
      return (
        <QuestionView AnswerInputTypes={AnswerInputTypes} question={question} />
      )
    }

    if (isPreviewing) {
      return (
        <StyledSpinner>
          <CircularProgress size={32} />
        </StyledSpinner>
      )
    }

    return (
      <QuestionView
        AnswerInputTypes={AnswerInputTypes}
        question={previewQuestion ?? question}
      />
    )
  }

  return (
    <PuiPopup open={open} {...rest}>
      <StyledContainer container direction="column" p={3}>
        <Grid item>
          <Text variant="h2">
            {t('Admin:CATALOG.PREVIEW_QUESTION_POPUP.HEADER')}
          </Text>
        </Grid>
        {renderQuestionContent()}
      </StyledContainer>
    </PuiPopup>
  )
}

export default PreviewQuestionPopup
